import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { mainSiteUrl, categories } from '@/mocks/categories';

export default function Header() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b-2 border-background-200 bg-background-50">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" aria-label={t('nav.brand')}>
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-links-line text-base" aria-hidden="true"></i>
          </span>
          <span className="font-heading text-xl font-extrabold tracking-tight text-foreground-950">
            {t('nav.brand')}
          </span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `text-sm font-semibold transition-colors whitespace-nowrap ${
                isActive ? 'text-primary-700' : 'text-foreground-800 hover:text-foreground-950'
              }`
            }
          >
            {t('nav.home')}
          </NavLink>
          <NavLink
            to="/guides/usfans-buying-guide"
            className={({ isActive }) =>
              `text-sm font-semibold transition-colors whitespace-nowrap ${
                isActive ? 'text-primary-700' : 'text-foreground-800 hover:text-foreground-950'
              }`
            }
          >
            {t('nav.shoppingGuide')}
          </NavLink>
          <NavLink
            to="/usfans-spreadsheet-guide"
            className={({ isActive }) =>
              `text-sm font-semibold transition-colors whitespace-nowrap ${
                isActive ? 'text-primary-700' : 'text-foreground-800 hover:text-foreground-950'
              }`
            }
          >
            {t('nav.guide')}
          </NavLink>
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher />
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            {t('nav.visitMain')}
          </a>
        </div>

        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-900 md:hidden cursor-pointer"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} aria-hidden="true"></i>
        </button>
      </div>

      {menuOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            <NavLink
              to="/"
              end
              onClick={() => setMenuOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t('nav.home')}
            </NavLink>
            <NavLink
              to="/guides/usfans-buying-guide"
              onClick={() => setMenuOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t('nav.shoppingGuide')}
            </NavLink>
            <NavLink
              to="/usfans-spreadsheet-guide"
              onClick={() => setMenuOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t('nav.guide')}
            </NavLink>

            <button
              type="button"
              onClick={() => setCatsOpen((v) => !v)}
              className="flex items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              <span>{t('dropdown.label')}</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform ${catsOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
            </button>

            {catsOpen && (
              <div className="mt-1 grid grid-cols-2 gap-1 rounded-lg bg-background-100 p-2">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-50"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className="ri-arrow-right-up-line text-xs" aria-hidden="true"></i>
                    </span>
                    <span className="whitespace-nowrap">{t(`categories.${cat.key}`)}</span>
                  </a>
                ))}
              </div>
            )}
          </nav>
          <div className="mt-3 flex items-center justify-between gap-3">
            <LanguageSwitcher />
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap"
            >
              {t('nav.visitMain')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}